import { useDispatch, useSelector } from 'react-redux'
import { RegModalWrapper } from '../ModalContent'
import pxToRem from '../../../../helpers/pxToRem'
import styles from '../index.module.scss'
import { Stack } from '@mui/material'
import { UserRegSx } from '../../../../components/TextField'
import { RootState } from '../../../../redux/store'
import { getIncompleteState } from '../../ReviewPage/helper'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { teacherRegSchema } from '../../../../schemas/teacherReg'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormInputText from '../../../../components/FormComponents/FormInputText'
import FormInputContact from '../../../../components/FormComponents/FormInputContact'
import { update, ref } from 'firebase/database'
import { db } from '../../../../firebase/configs'

const baseSchema = teacherRegSchema.shape.emergencyContacts.pick({
    primary: true,
    secondary: true,
})
type FormType = z.infer<typeof baseSchema>

const EmergencyContact = () => {
    const dispatch = useDispatch()
    const reg = useSelector((state: RootState) => state.userReg.fields)
    const userId = useSelector(
        (state: RootState) => state.user.auth.status?.uid
    )

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty },
    } = useForm({
        resolver: zodResolver(baseSchema),
        mode: 'onChange',
        defaultValues: {
            primary: {
                name: reg.firstEmergencyContact?.name || '',
                relationship: reg.firstEmergencyContact?.relationship || '',
                contact: reg.firstEmergencyContact?.contact || '',
            },
            secondary: {
                name: reg.secondEmergencyContact?.name || '',
                relationship: reg.secondEmergencyContact?.relationship || '',
                contact: reg.secondEmergencyContact?.contact || '',
            },
        },
    })

    const handleUpdate = async (data: FormType) => {
        try {
            if (!userId) throw new Error('User is not authenticated.')

            if (!isDirty) return

            const { primary, secondary } = data

            await update(ref(db, `teachers/${userId}`), {
                firstEmergencyContact: {
                    ...primary,
                    state: 'submitted',
                },
                secondEmergencyContact: {
                    ...secondary,
                    state: 'submitted',
                },
            })
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    return (
        <>
            <RegModalWrapper
                title="Emergency Contacts"
                handleUpdate={handleSubmit(handleUpdate)}
                state={getIncompleteState({
                    firstEmergencyContact: reg.firstEmergencyContact,
                    secondEmergencyContact: reg.secondEmergencyContact,
                })}
                isAllComplete={isValid}
            >
                <Stack gap={pxToRem(24)} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <h5 className={styles.h5}>Emergency Contacts</h5>
                        <p className={styles.p}>
                            Your emergency contacts will be shared with schools
                            or centres that confirm you to work in the event
                            that an emergency occurs.
                        </p>
                    </Stack>
                    <p className={styles.emergencySubtitle}>Primary Contact</p>
                    <Stack gap={pxToRem(24)}>
                        <Stack direction="row" gap={pxToRem(24)}>
                            <FormInputText
                                name="primary.name"
                                label="Name"
                                control={control}
                                sx={{ ...UserRegSx, flex: 1 }}
                            />
                            <FormInputText
                                name="primary.relationship"
                                label="Relationship"
                                control={control}
                                sx={{ ...UserRegSx, flex: 1 }}
                            />
                        </Stack>
                        <FormInputContact
                            name="primary.contact"
                            label="Contact"
                            control={control}
                            sx={{ ...UserRegSx, flex: 1 }}
                        />
                    </Stack>
                    <p className={styles.emergencySubtitle}>
                        Secondary Contact
                    </p>
                    <Stack gap={pxToRem(24)}>
                        <Stack direction="row" gap={pxToRem(24)}>
                            <FormInputText
                                name="secondary.name"
                                label="Name"
                                control={control}
                                sx={{ ...UserRegSx, flex: 1 }}
                            />
                            <FormInputText
                                name="secondary.relationship"
                                label="Relationship"
                                control={control}
                                sx={{ ...UserRegSx, flex: 1 }}
                            />
                        </Stack>
                        <FormInputContact
                            name="secondary.contact"
                            label="Contact"
                            control={control}
                            sx={{ ...UserRegSx, flex: 1 }}
                        />
                    </Stack>
                </Stack>
            </RegModalWrapper>
        </>
    )
}

export default EmergencyContact
